import {
  Component,
  ElementRef,
  Injectable,
  Input,
  OnInit,
  ViewChild
} from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms'
import {
  CountryCallingCode,
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumber
} from 'libphonenumber-js/max'

import { DateAdapter } from '@angular/material/core'
import {
  MAT_LEGACY_DATE_FORMATS as MAT_DATE_FORMATS,
  LegacyNativeDateAdapter as NativeDateAdapter
} from '@angular/material/legacy-core'
import {
  CptSharedDataService,
  TransifexService
} from '@inside-hub-app/customer-portal-shared'
import { B2CConfig } from '@inside-hub-app/customer-portal-config'
import {
  isDuplicateUserValidationError,
  isUserUpdateAddressValidationError,
  UserService,
  UserUpdateAddressValidationError
} from '@inside-hub-app/customer-portal-b2c-client'
import * as moment from 'moment'
import { delay, merge } from 'rxjs'
import { KcCustomCalendarHeaderComponent } from '../../../components/kc-custom-calendar-header/kc-custom-calendar-header.component'
import {
  KcDataService
} from '../../../kc-data.service'
import { InstantErrorStateMatcherService } from '../../../services/instant-error-state-matcher.service'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { KcClientData, KcPersonalizeNewViewData } from '../../../interfaces/kc-data.interface'
@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  private static _to2digit(n: number) {
    return ('00' + n).slice(-2)
  }

  override getFirstDayOfWeek(): number {
    return 1
  }

  override parse(value: any): Date | null {
    if (typeof value === 'string' && value.includes('.')) {
      const str = value.split('.')
      const year = Number(str[2])
      const month = Number(str[1]) - 1
      const date = Number(str[0])
      return new Date(year, month, date)
    }
    const timestamp = typeof value === 'number' ? value : Date.parse(value)
    return isNaN(timestamp) ? null : new Date(timestamp)
  }

  override format(date: Date, displayFormat: string): string {
    if (displayFormat === 'input') {
      const day = date.getDate()
      const month = date.getMonth() + 1
      const year = date.getFullYear()
      return (
        AppDateAdapter._to2digit(day) +
        '.' +
        AppDateAdapter._to2digit(month) +
        '.' +
        year
      )
    } else if (displayFormat === 'inputMonth') {
      moment.locale('de')
      return moment(date).format('MMMM YYYY')
    } else {
      return date.toDateString()
    }
  }
}

export const APP_DATE_FORMATS = {
  parse: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' }
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'inputMonth',
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
}

export interface AddressSuggestionChoice {
  street?: string
  streetNumber?: string
  streetAdditional?: string
  city?: string
  zip?: string
}

@Component({
  selector: 'customer-portal-revolution-personalize-new',
  templateUrl: './revolution-personalize-new.component.html',
  providers: [
    {
      provide: DateAdapter,
      useClass: AppDateAdapter
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: APP_DATE_FORMATS
    }
  ]
})
export class RevolutionPersonalizeNewComponent implements OnInit {
  @Input() view: string
  matcher = new InstantErrorStateMatcherService()
  customHeader = KcCustomCalendarHeaderComponent
  addressHints: AddressSuggestionChoice[]
  notValid = false
  addressRequired = false

  firstNameRegex: string
  lastNameRegex: string
  addressStreetRegex: string
  addressStreetNumberRegex: string
  addressStreetAdditionalRegex: string
  companyNameRegex: string
  addressZipRegex: string
  addressCityRegex: string
  constructor(
    private readonly userService: UserService,
    private readonly kcDataService: KcDataService,
    private readonly kcSharedDataService: CptSharedDataService,
    public transifexService: TransifexService,
    public remoteConfigService: EfRemoteConfigurationService<B2CConfig>
  ) {
    this.firstNameRegex = this.remoteConfigService.get(
      'regEx.nameRegEx.firstName.pattern'
    )
    this.lastNameRegex = this.remoteConfigService.get(
      'regEx.nameRegEx.lastName.pattern'
    )
    this.addressStreetRegex = this.remoteConfigService.get(
      'regEx.addressRegEx.street.pattern'
    )
    this.addressStreetNumberRegex = this.remoteConfigService.get(
      'regEx.addressRegEx.streetNumber.pattern'
    )
    this.addressStreetAdditionalRegex = this.remoteConfigService.get(
      'regEx.addressRegEx.streetAdditional.pattern'
    )
    this.companyNameRegex = this.remoteConfigService.get(
      'regEx.addressRegEx.companyName.pattern'
    )
    this.addressZipRegex = this.remoteConfigService.get(
      'regEx.addressRegEx.zip.pattern'
    )
    this.addressCityRegex = this.remoteConfigService.get(
      'regEx.addressRegEx.city.pattern'
    )

    this.mobilePrefixCountries =
    this.remoteConfigService.get('b2c.mobileCountryPrefixes')

    this.form = new FormGroup(
      {
        accountType: new FormControl<string | null>('personal'),
        salutation: new FormControl<string | null>(null),
        title: new FormControl<string | null>(null),
        firstName: new FormControl<string | null>(
          null,
          Validators.compose([
            Validators.required,
            Validators.pattern(this.firstNameRegex),
            Validators.maxLength(
              this.remoteConfigService.get('regEx.nameRegEx.firstName.maxLength')
            )
          ])
        ),
        lastName: new FormControl<string | null>(
          null,
          Validators.compose([
            Validators.required,
            Validators.pattern(this.lastNameRegex),
            Validators.maxLength(
              this.remoteConfigService.get('regEx.nameRegEx.lastName.maxLength')
            )
          ])
        ),
        companyName: new FormControl<string | null>(
          { value: '', disabled: true },
          Validators.compose([Validators.pattern(this.companyNameRegex)])
        ),
        birthday: new FormControl<string | null>(null),
        language: new FormControl<string | null>(null),
        country: new FormControl<string | null>(null),
        street: new FormControl<string | null>(
          null,
          Validators.compose([
            Validators.pattern(this.addressStreetRegex),
            Validators.maxLength(
              this.remoteConfigService.get('regEx.addressRegEx.street.maxLength')
            )
          ])
        ),
        streetNumber: new FormControl<string | null>(
          null,
          Validators.compose([Validators.pattern(this.addressStreetNumberRegex)])
        ),
        streetAdditional: new FormControl<string | null>(
          null,
          Validators.compose([
            Validators.pattern(this.addressStreetAdditionalRegex),
            Validators.maxLength(
              this.remoteConfigService.get(
                'regEx.addressRegEx.streetAdditional.maxLength'
              )
            )
          ])
        ),
        zip: new FormControl<string | null>(
          null,
          Validators.compose([
            Validators.pattern(this.addressZipRegex),
            Validators.minLength(
              this.remoteConfigService.get('regEx.addressRegEx.zip.minLength')
            ),
            Validators.maxLength(
              this.remoteConfigService.get('regEx.addressRegEx.zip.maxLength')
            )
          ])
        ),
        city: new FormControl<string | null>(
          null,
          Validators.compose([
            Validators.pattern(this.addressCityRegex),
            Validators.maxLength(
              this.remoteConfigService.get('regEx.addressRegEx.city.maxLength')
            )
          ])
        ),
        mobilePrefix: new FormControl<string | null>(null),
        mobile: new FormControl<string | null>(null, [
          control => {
            if (!control || !this.form || !control.value) {
              return {}
            }
  
            if (!this.form.value.mobilePrefix) {
              return { invalidNumber: true }
            }
  
            if (control.value.length < 2) {
              return { invalidNumber: true }
            }
  
            if (
              control.value.length >=
              this.remoteConfigService.get('phoneNumber.length')
            ) {
              return { invalidNumber: true }
            }
  
            if (!Number(control.value)) {
              return { invalidNumber: true }
            }
  
            const telephoneNumber: string =
              this.form.value.mobilePrefix + control.value
  
            const prefix: string = this.form.value.mobilePrefix
            const prefixEntry =
              Object.entries(this.mobilePrefixes).find(entry => {
                return entry[1] === prefix.substr(1)
              }) != null || []
  
            const prefixCountry = prefixEntry[0] as CountryCode
  
            let parsed = null
            try {
              parsed = parsePhoneNumber(telephoneNumber, prefixCountry)
            } catch {
              return { invalidNumber: true }
            }
  
            if (!parsed.isPossible()) {
              return { invalidNumber: true }
            }
  
            if (!parsed.isValid()) {
              return { invalidNumber: true }
            }
  
            const numberType = parsed.getType()
  
            if (numberType !== 'MOBILE') {
              return { notMobile: true }
            }
            return {}
          }
        ])
      },
      [
        () => {
          const errors: any = {}
  
          if (this.addressValidationError) {
            errors.invalidAddress = true
          }
          if (this.duplicateUserValidationError) {
            errors.duplicateUser = true
          }
  
          return errors
        }
      ]
    )
  }

  public errors = {
    pattern: null
  }

  @ViewChild('personalizeNewForm', { static: true })
  private readonly formRef: ElementRef

  public form

  public clientData: KcClientData
  public viewData: KcPersonalizeNewViewData
  private validationError: any
  public duplicateUserValidationError: any
  public showDuplicateUserValidationError = false
  private addressValidationError: UserUpdateAddressValidationError
  public currentAddress: AddressSuggestionChoice
  public correctedAddress: AddressSuggestionChoice
  private readonly mobilePrefixCountries: string[]
  public mobilePrefixes: { [key: string]: CountryCallingCode } = {}
  public salutations: any
  public titles: any
  public salutationsWithTitles: string | any[]
  public submitting: boolean
  public telephone: string | null
  public forceSave = false
  public streetNumFalse = false
  public showForceSaveError = false
  public maxDate: Date
  private readonly icon = 'assets/icons/create_24px_outlined.svg'
  iconUrl = ''

  ngOnInit() {
    this.clientData = this.kcDataService.getClientData()
    this.viewData = this.kcDataService.getViewData() as KcPersonalizeNewViewData
    this.getMobilePrefixAndPhoneNumber()

    this.iconUrl = `${this.kcSharedDataService.getResourcesPath()}/${this.icon}`

    // restrict 18 yo
    const today = new Date()
    this.maxDate = new Date(today)
    this.maxDate.setFullYear(this.maxDate.getFullYear() - 18)

    this.titles = JSON.parse(this.viewData.titles || '')
    this.salutations = JSON.parse(this.viewData.salutations || '')
    this.salutationsWithTitles = JSON.parse(
      this.viewData.salutationsWithTitles || ''
    )
    this.onFormReady()
  }

  private getMobilePrefixAndPhoneNumber() {
    // validate mobile after changing prefix
    // validate mobile after changing prefix
    this.form.controls.mobilePrefix.valueChanges
      .pipe(delay(1))
      .subscribe(prefix => {
        this.form.controls.mobile.markAsDirty()
        this.form.controls.mobile.updateValueAndValidity()
      })

    this.form.controls.mobile.valueChanges.pipe(delay(10)).subscribe(prefix => {
      if (this.form.controls.mobile.value) {
        this.form.controls.mobile.markAsDirty()
        this.form.controls.mobile.markAsTouched()
        this.form.controls.mobile.updateValueAndValidity()
      }
    })

    // fill calling codes
    for (const countryCodeIndex in this.mobilePrefixCountries) {
      const countryCode = this.mobilePrefixCountries[countryCodeIndex]
      this.mobilePrefixes[countryCode] = getCountryCallingCode(
        countryCode as CountryCode
      )
    }

    // sometimes we get undefined string as value
    if (this.viewData?.formData?.telephoneNumber === 'undefined') {
      this.viewData.formData.telephoneNumber = undefined
    }

    // get prefix and number
    if (this.viewData?.formData?.telephoneNumber) {
      const parsedPhoneNumber = parsePhoneNumber(
        this.viewData?.formData?.telephoneNumber
      )

      // fill prefix and number
      this.form.patchValue({
        mobilePrefix: parsedPhoneNumber
          ? '+' + parsedPhoneNumber.countryCallingCode
          : '',
        mobile: parsedPhoneNumber
          ? parsedPhoneNumber.nationalNumber.toString()
          : ''
      })
    }
  }

  private onFormReady() {
    // sometimes we get undefined string as value
    if (this.viewData?.formData?.telephoneNumber === 'undefined') {
      this.viewData.formData.telephoneNumber = undefined
    }

    // initial values
    this.form.patchValue({
      salutation: this.viewData.formData.salutation,
      title: this.viewData.formData.title || 'unknown',
      firstName: this.viewData.formData.firstName,
      lastName: this.viewData.formData.lastName,
      street: this.viewData.formData.street,
      streetNumber: this.viewData.formData.streetNumber,
      streetAdditional: this.viewData.formData.streetAdditional || '',
      zip: this.viewData.formData.zip,
      city: this.viewData.formData.city,
      birthday: moment(this.viewData.formData.birthDate || null).toISOString()
    })

    // Add validators based on ENV variables
    if (this.remoteConfigService.get('registrationRequiredFields.street')) {
      this.addFormControlValidator(
        this.form.controls.street,
        Validators.required
      )
    }
    if (
      this.remoteConfigService.get('registrationRequiredFields.streetNumber')
    ) {
      this.addFormControlValidator(
        this.form.controls.streetNumber,
        Validators.required
      )
    }
    if (this.remoteConfigService.get('registrationRequiredFields.zip')) {
      this.addFormControlValidator(this.form.controls.zip, Validators.required)
    }
    if (this.remoteConfigService.get('registrationRequiredFields.city')) {
      this.addFormControlValidator(this.form.controls.city, Validators.required)
    }
    if (this.remoteConfigService.get('registrationRequiredFields.birthday')) {
      this.addFormControlValidator(
        this.form.controls.birthday,
        Validators.required
      )
    }
    if (
      this.remoteConfigService.get('registrationRequiredFields.mobilePrefix')
    ) {
      this.addFormControlValidator(
        this.form.controls.mobilePrefix,
        Validators.required
      )
    }
    if (
      this.remoteConfigService.get('registrationRequiredFields.mobileNumber')
    ) {
      this.addFormControlValidator(
        this.form.controls.mobile,
        Validators.required
      )
    }

    // salutation and title are available
    this.onSalutationChange()

    // address suggestion
    if (this.viewData.validationFailure) {
      this.validationError = JSON.parse(this.viewData.validationFailure)

      if (isUserUpdateAddressValidationError(this.validationError)) {
        this.addressValidationError = this
          .validationError as UserUpdateAddressValidationError

        this.addressHints = null
        this.form.updateValueAndValidity()

        if (
          this.addressValidationError.fields != null &&
          this.addressValidationError.fields.length > 0 &&
          this.addressValidationError.fields.find(
            s => s.id === 'address-hints'
          ) != null
        ) {
          this.addressHints = this.addressValidationError.fields[0].addressHints
          this.notValid = true
        }

        if (
          this.addressValidationError.fields != null &&
          this.addressValidationError.fields.length > 0
        ) {
          this.currentAddress = {
            street: this.viewData.formData.street,
            streetNumber: this.viewData.formData.streetNumber,
            streetAdditional: this.viewData.formData.streetAdditional,
            city: this.viewData.formData.city,
            zip: this.viewData.formData.zip
          }

          const cityField = this.addressValidationError.fields.find(
            field => field.id === 'city'
          )
          const zipField = this.addressValidationError.fields.find(
            field => field.id === 'zip'
          )
          const streetField = this.addressValidationError.fields.find(
            field => field.id === 'street'
          )
          const streetNumberField = this.addressValidationError.fields.find(
            field => field.id === 'street-number'
          )
          const streetAdditionalField = this.addressValidationError.fields.find(
            field => field.id === 'street-additional'
          )

          if (
            this.addressValidationError.fields &&
            this.addressValidationError.fields.length === 1 &&
            this.addressValidationError.fields.find(
              field => field.id === 'street-number'
            ) != null
          ) {
            this.showForceSaveError = false
            this.forceSave = true
            this.streetNumFalse = true
          }

          this.correctedAddress = {
            street:
              streetField != null
                ? streetField.correction
                : this.currentAddress.street,
            streetNumber: streetNumberField?.correction
              ? streetNumberField.correction
              : this.currentAddress.streetNumber,
            streetAdditional:
              streetAdditionalField != null
                ? streetAdditionalField.correction
                : this.currentAddress.streetAdditional,
            city:
              cityField != null
                ? cityField.correction
                : this.currentAddress.city,
            zip:
              zipField != null ? zipField.correction : this.currentAddress.zip
          }
        } else if (
          (this.addressValidationError.fields != null &&
            this.addressValidationError.fields.length === 0) ||
          this.addressValidationError.fields == null
        ) {
          this.forceSave = true
          this.showForceSaveError = true
          this.form.updateValueAndValidity()
        }
      } else if (
        isDuplicateUserValidationError(this.validationError) ||
        this.validationError?.reason === 'duplicateContact' ||
        this.validationError?.reason === 'duplicateContacts' ||
        this.validationError?.reasons === 'duplicateContact' ||
        this.validationError?.reasons === 'duplicateContacts'
      ) {
        const message = this.kcDataService.getMessage()
        if (message == null || message?.type !== 'error') {
          this.showDuplicateUserValidationError = true
        }
        this.duplicateUserValidationError = this.validationError
        this.form.updateValueAndValidity()
      }
    }

    // If 0 is in the first letter, remove it on post request
    this.form.valueChanges.subscribe(value => {
      if (value?.mobile?.[0]) {
        this.telephone =
          value.mobilePrefix +
          (value.mobile[0] === '0' ? value.mobile.substr(1) : value.mobile)
      } else {
        this.telephone = undefined
      }
    })

    // clear address validation error on change
    merge(
      this.form.controls.street.valueChanges,
      this.form.controls.streetNumber.valueChanges,
      this.form.controls.streetAdditional.valueChanges,
      this.form.controls.city.valueChanges,
      this.form.controls.zip.valueChanges
    ).subscribe(() => {
      this.duplicateUserValidationError = null
      this.addressValidationError = null
      this.forceSave = false
      this.form.updateValueAndValidity()
    })

    // Set all adress fileds required if any of them entered
    merge(
      this.form.controls.street.valueChanges,
      this.form.controls.streetNumber.valueChanges,
      this.form.controls.city.valueChanges,
      this.form.controls.zip.valueChanges
    ).subscribe(() => {
      this.setAddressFieldsRequired()
      this.form.updateValueAndValidity()
    })

    // clear duplicate user validation error
    merge(
      this.form.controls.title.valueChanges,
      this.form.controls.salutation.valueChanges,
      this.form.controls.firstName.valueChanges,
      this.form.controls.lastName.valueChanges,
      this.form.controls.mobilePrefix.valueChanges,
      this.form.controls.mobile.valueChanges
    ).subscribe((val: any) => {
      this.addressValidationError = null
      this.duplicateUserValidationError = null
      this.form.updateValueAndValidity()
    })
  }

  public onSalutationChange() {
    if (
      this.salutationsWithTitles.includes(this.form.controls.salutation.value)
    ) {
      this.form.controls.title.enable()
    } else {
      this.form.patchValue({ title: 'unknown' }, { emitEvent: false })
      this.form.controls.title.disable()
    }
  }

  public save() {
    if (
      this.form.controls.street.value !== this.viewData.formData.street ||
      this.form.controls.streetNumber.value !==
        this.viewData.formData.streetNumber ||
      this.form.controls.streetAdditional.value !==
        this.viewData.formData.streetAdditional ||
      this.form.controls.city.value !== this.viewData.formData.city ||
      this.form.controls.zip.value !== this.viewData.formData.zip
    ) {
      this.forceSave = false
      this.form.updateValueAndValidity()
    }

    this.form.updateValueAndValidity()

    this.submitting = true

    // submit form
    const formElement = this.formRef.nativeElement as HTMLFormElement
    formElement.submit()
  }

  public correctAddress() {
    this.form.patchValue({
      street: this.correctedAddress.street,
      streetNumber: this.correctedAddress.streetNumber,
      streetAdditional: this.correctedAddress.streetAdditional,
      city: this.correctedAddress.city,
      zip: this.correctedAddress.zip
    })

    this.addressValidationError = null
    this.correctedAddress = null
    this.form.updateValueAndValidity()
  }

  useAddressHint(address: AddressSuggestionChoice) {
    this.form.patchValue({
      street: address.street,
      streetNumber: address.streetNumber,
      streetAdditional: address.streetAdditional,
      city: address.city,
      zip: address.zip
    })

    this.notValid = false
    this.addressValidationError = null
    this.correctedAddress = null
    this.addressHints = null
    this.form.updateValueAndValidity()
  }

  setAddressFieldsRequired() {
    if (
      this.form.controls.street.value ||
      this.form.controls.streetNumber.value ||
      this.form.controls.city.value ||
      this.form.controls.zip.value
    ) {
      this.addressRequired = true
    } else {
      this.addressRequired = false
    }
  }

  // get existing validators and assign a new validator
  addFormControlValidator(
    formControl: AbstractControl,
    newValidator: ValidatorFn
  ) {
    const validators: ValidatorFn[] =
      formControl.validator != null
        ? [formControl.validator, newValidator]
        : [newValidator]
    formControl.setValidators(validators)
  }
}
